import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AdminUser, LoginRequest } from '@redex/shared';
import { authService } from '../services/authService';
import { getRecaptchaToken } from '../hooks/useRecaptcha';
import toast from 'react-hot-toast';

// Extended login request with reCAPTCHA
interface ExtendedLoginRequest extends LoginRequest {
  recaptchaToken?: string;
}

// Types
interface AuthState {
  user: Omit<AdminUser, 'passwordHash'> | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: Omit<AdminUser, 'passwordHash'>; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESTORE_SESSION'; payload: { user: Omit<AdminUser, 'passwordHash'>; token: string } };

interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const token = localStorage.getItem('redex_admin_token');
        if (!token) {
          dispatch({ type: 'LOGOUT' });
          return;
        }

        // Verify token with backend
        const user = await authService.verifyToken(token);
        
        if (user) {
          dispatch({
            type: 'RESTORE_SESSION',
            payload: { user, token }
          });
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('redex_admin_token');
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        console.error('Session restoration failed:', error);
        localStorage.removeItem('redex_admin_token');
        dispatch({ type: 'LOGOUT' });
      }
    };

    restoreSession();
  }, []);

  // Login function
  const login = async (credentials: LoginRequest) => {
    try {
      dispatch({ type: 'LOGIN_START' });

      // Execute reCAPTCHA verification
      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await getRecaptchaToken('LOGIN');
      } catch (recaptchaError) {
        console.warn('reCAPTCHA failed, proceeding without token:', recaptchaError);
      }

      const response = await authService.login({
        ...credentials,
        recaptchaToken,
      });
      
      if (response.success && response.user && response.token) {
        // Store token in localStorage
        localStorage.setItem('redex_admin_token', response.token);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: response.user,
            token: response.token
          }
        });

        toast.success(`Welcome back, ${response.user.name}!`);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage
      });
      
      toast.error(errorMessage);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('redex_admin_token');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher-order component for protected routes
export function withAuth<P extends object>(Component: React.ComponentType<P>) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // This will be handled by the Router logic
      return null;
    }

    return <Component {...props} />;
  };
}

export default AuthContext;
